<template>
    <div class="a-table__wrapper" :class="`a-table__wrapper_type-${l10n.settings.type}`">
        <div class="a-table" :class="className">
            <a-dangerous-html
                v-if="l10n.settings.lead?.length"
                class="a-table__lead"
                :content="l10n.settings.lead"
            />
            <!-- Conditional scroll container for type-1 tables -->
            <div v-if="l10n.settings.type === 1" class="a-table__scroll-container" tabindex="0">
                <div ref="headRef" class="a-table__head syncscroll" name="table-part">
                    <div
                        class="a-table__head-row"
                        :class="{ 'a-table__head-row_no-scroll': l10n.settings.headMobileType === 'no-scroll' }"
                    >
                        <s-table-head-switcher
                            v-if="l10n.settings.switcher"
                            v-model="withOptionalRows"
                            :text="l10n.settings.switcher"
                        />
                        <div
                            v-for="(cell, index) in l10n.head"
                            :key="`table-head-cell-${index}`"
                            class="a-table__head-cell"
                            :class="{
                                ['a-table__cell_colspan_' + cell.colspan]: cell.colspan,
                                ['a-table__cell_align_' + cell.align]: cell.align
                            }"
                        >
                            <s-table-head-cell :cell="cell" is-type-one />
                        </div>
                    </div>
                </div>
                <table ref="bodyRef" class="a-table__body syncscroll" name="table-part">
                    <tbody class="syncscroll">
                        <tr
                            v-for="(row, rowIndex) in l10n.rows"
                            :key="`table-row-${rowIndex}`"
                            class="a-table__row"
                            :class="getClassNameForTableRow(row)"
                        >
                            <td
                                v-for="(cell, cellIndex) in row.content"
                                :key="`table-cell-${cellIndex}`"
                                class="a-table__cell"
                                :class="getClassNameForTableCell(cell)"
                            >
                                <div v-if="effectiveMobileType === 'column'" class="a-table__head-mobile">
                                    <s-table-mobile-head-cell :head-cell="l10n.head[cellIndex]" />
                                </div>
                                <template v-if="isTextCell(cell) && !cell.expand">
                                    <div :class="[`a-table__${cell.type}`]">
                                        <a-glyph
                                            v-if="cell.glyph"
                                            class="a-table__glyph"
                                            :name="cell.glyph"
                                            :fill="cell.fill"
                                        />
                                        <template v-if="cell.productPageCTA">
                                            <a-product-cta
                                                :settings="{ hasDescription: true, ...cell.productPageCTA }"
                                            />
                                        </template>
                                        <template v-else>
                                            <a-link
                                                v-if="cell.to"
                                                v-bind="cell"
                                                class="a-table__category-link"
                                                :text="cell.val"
                                                :size="cell.size || 'paragraph'"
                                            />
                                            <span v-else v-html="cell.val" />
                                        </template>
                                        <a-tooltip v-if="cell.tooltip" class="a-table__tooltip" :content="cell.tooltip" />
                                    </div>
                                </template>
                                <template v-if="cell.expand">
                                    <div
                                        :class="[`a-table__${cell.type}`, `a-table__expand`]"
                                        @click="toggleExpand(cellIndex, rowIndex)"
                                    >
                                        <a-glyph
                                            :name="isExpand(cellIndex, rowIndex) ? 'chevron-big-down' : 'i-chevron-big-right--16'"
                                            class="toggleIcon"
                                        />
                                        <template v-if="cell.productPageCTA">
                                            <a-product-cta
                                                :settings="{ hasDescription: true, ...cell.productPageCTA }"
                                            />
                                        </template>
                                        <template v-else>
                                            <a-link
                                                v-if="cell.to"
                                                v-bind="cell"
                                                class="a-table__category-link"
                                                :text="cell.val"
                                                :size="cell.size || 'paragraph'"
                                            />
                                            <span v-else v-html="cell.val" />
                                        </template>
                                        <template v-if="cell.tooltips">
                                            <a-tooltip
                                                v-for="tooltip in cell.tooltips"
                                                :key="tooltip.text"
                                                :glyph="tooltip.glyph"
                                                class="a-table__tooltip"
                                                :content="tooltip.text"
                                            />
                                        </template>
                                        <a-tooltip v-if="cell.tooltip" class="a-table__tooltip" :content="cell.tooltip" />
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'list'">
                                    <div class="a-table__list-wrap">
                                        <ul class="a-table__list">
                                            <li v-for="(item, i) in cell.list" :key="i" class="a-table__list-item">
                                                {{ item }}
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'checked'">
                                    <div class="a-table__check">
                                        <div v-if="l10n.translated?.checked" class="a-table__check-text">
                                            {{ l10n.translated.checked[cell.val] }}
                                        </div>
                                        <div v-else class="a-table__check-icon" :class="[`a-table__check-icon_${cell.val}`]" />
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'button'">
                                    <div class="a-table__button-wrapper">
                                        <a-button
                                            v-modal="getButtonModalId(cell.val)"
                                            v-bind="cell.val"
                                            :to="getButtonModalId(cell.val) ? null : cell.val.link"
                                        />
                                    </div>
                                </template>
                                <template v-if="cell.description && (!cell.expand || isExpand(cellIndex, rowIndex))">
                                    <div
                                        class="a-table__description"
                                        :class="{ 'a-table__description-full': cell.description.full }"
                                    >
                                        <a-icon
                                            v-if="cell.description.icon"
                                            class="a-table__description-icon"
                                            icon-variant
                                            :number="cell.description.icon"
                                        />
                                        <div v-if="cell.description.title" class="a-table__description-title">
                                            {{ cell.description.title }}
                                        </div>
                                        <div v-if="cell.description.text" class="a-table__description-text">
                                            {{ cell.description.text }}
                                        </div>
                                        <a-link
                                            v-if="cell.description.link"
                                            class="a-table__description-link"
                                            v-bind="cell.description.link"
                                        />
                                        <div
                                            v-if="cell.description.note"
                                            class="a-table__description-note"
                                        >
                                            {{ cell.description.note }}
                                        </div>
                                    </div>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <template v-else>
                <div ref="headRef" class="a-table__head syncscroll" name="table-part">
                    <div
                        class="a-table__head-row"
                        :class="{ 'a-table__head-row_no-scroll': l10n.settings.headMobileType === 'no-scroll' }"
                    >
                        <s-table-head-switcher
                            v-if="l10n.settings.switcher"
                            v-model="withOptionalRows"
                            :text="l10n.settings.switcher"
                        />
                        <div
                            v-for="(cell, index) in l10n.head"
                            :key="`table-head-cell-${index}`"
                            class="a-table__head-cell"
                            :class="{
                                ['a-table__cell_colspan_' + cell.colspan]: cell.colspan,
                                ['a-table__cell_align_' + cell.align]: cell.align
                            }"
                        >
                            <s-table-head-cell :cell="cell" />
                        </div>
                    </div>
                </div>
                <table ref="bodyRef" class="a-table__body syncscroll" name="table-part">
                    <tbody>
                        <tr
                            v-for="(row, rowIndex) in l10n.rows"
                            :key="`table-row-${rowIndex}`"
                            class="a-table__row"
                            :class="getClassNameForTableRow(row)"
                        >
                            <td
                                v-for="(cell, cellIndex) in row.content"
                                :key="`table-cell-${cellIndex}`"
                                class="a-table__cell"
                                :class="getClassNameForTableCell(cell)"
                            >
                                <div v-if="effectiveMobileType === 'column'" class="a-table__head-mobile">
                                    <s-table-mobile-head-cell :head-cell="head[cellIndex]" />
                                </div>
                                <template v-if="isTextCell(cell) && !cell.expand">
                                    <div :class="[`a-table__${cell.type}`]">
                                        <a-glyph
                                            v-if="cell.glyph"
                                            class="a-table__glyph"
                                            :name="cell.glyph"
                                            :fill="cell.fill"
                                        />
                                        <template v-if="cell.productPageCTA">
                                            <a-product-cta
                                                :settings="{ hasDescription: true, ...cell.productPageCTA }"
                                            />
                                        </template>
                                        <template v-else>
                                            <a-link
                                                v-if="cell.to"
                                                v-bind="cell"
                                                class="a-table__category-link"
                                                :text="cell.val"
                                                :size="cell.size || 'paragraph'"
                                            />
                                            <span v-else v-html="cell.val" />
                                        </template>
                                        <a-tooltip v-if="cell.tooltip" class="a-table__tooltip" :content="cell.tooltip" />
                                    </div>
                                </template>
                                <template v-if="cell.expand">
                                    <div
                                        :class="[`a-table__${cell.type}`, `a-table__expand`]"
                                        @click="toggleExpand(cellIndex, rowIndex)"
                                    >
                                        <a-glyph
                                            :name="isExpand(cellIndex, rowIndex) ? 'chevron-big-down' : 'i-chevron-big-right--16'"
                                            class="toggleIcon"
                                        />
                                        <template v-if="cell.productPageCTA">
                                            <a-product-cta
                                                :settings="{ hasDescription: true, ...cell.productPageCTA }"
                                            />
                                        </template>
                                        <template v-else>
                                            <a-link
                                                v-if="cell.to"
                                                v-bind="cell"
                                                class="a-table__category-link"
                                                :text="cell.val"
                                                :size="cell.size || 'paragraph'"
                                            />
                                            <span v-else v-html="cell.val" />
                                        </template>
                                        <template v-if="cell.tooltips">
                                            <a-tooltip
                                                v-for="tooltip in cell.tooltips"
                                                :key="tooltip.text"
                                                :glyph="tooltip.glyph"
                                                class="a-table__tooltip"
                                                :content="tooltip.text"
                                            />
                                        </template>
                                        <a-tooltip v-if="cell.tooltip" class="a-table__tooltip" :content="cell.tooltip" />
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'list'">
                                    <div class="a-table__list-wrap">
                                        <ul class="a-table__list">
                                            <li v-for="(item, i) in cell.list" :key="i" class="a-table__list-item">
                                                {{ item }}
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'checked'">
                                    <div class="a-table__check">
                                        <div v-if="translated && translated.checked" class="a-table__check-text">
                                            {{ translated.checked[cell.val] }}
                                        </div>
                                        <div v-else class="a-table__check-icon" :class="[`a-table__check-icon_${cell.val}`]" />
                                    </div>
                                </template>
                                <template v-else-if="cell.type === 'button'">
                                    <div class="a-table__button-wrapper">
                                        <a-button
                                            v-modal="getButtonModalId(cell.val)"
                                            v-bind="cell.val"
                                            :to="getButtonModalId(cell.val) ? null : cell.val.link"
                                        />
                                    </div>
                                </template>
                                <template v-if="cell.description && (!cell.expand || isExpand(cellIndex, rowIndex))">
                                    <div
                                        class="a-table__description"
                                        :class="{ 'a-table__description-full': cell.description.full }"
                                    >
                                        <a-icon
                                            v-if="cell.description.icon"
                                            class="a-table__description-icon"
                                            icon-variant
                                            :number="cell.description.icon"
                                        />
                                        <div v-if="cell.description.title" class="a-table__description-title">
                                            {{ cell.description.title }}
                                        </div>
                                        <div v-if="cell.description.text" class="a-table__description-text">
                                            {{ cell.description.text }}
                                        </div>
                                        <a-link
                                            v-if="cell.description.link"
                                            class="a-table__description-link"
                                            v-bind="cell.description.link"
                                        />
                                        <div
                                            v-if="cell.description.note"
                                            class="a-table__description-note"
                                        >
                                            {{ cell.description.note }}
                                        </div>
                                    </div>
                                </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </template>
        </div>
        <a-modal
            v-for="ytId in youtubeIds"
            :id="`yt_id-${ytId}`"
            :key="ytId"
            type="youtube"
            is-youtube
            is-overlay-close
        >
            <a-youtube :yt-id="ytId" :params="{ autoplay: 1 }" />
        </a-modal>
        <a-demo-modal
            v-for="modal in demoModals"
            :key="modal.id"
            :modal="modal"
        />
    </div>
</template>

<script setup>
import { throttle } from 'lodash-es';
import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    onServerPrefetch,
    ref,
    watch,
} from 'vue';
import AButton from '@core/components/button/button.vue';
import ADangerousHtml from '@core/components/dangerous-html/dangerous-html.vue';
import ADemoModal from '@core/components/demo-modal/demo-modal.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import AIcon from '@core/components/icon/icon.vue';
import ALink from '@core/components/link/link.vue';
import AModal from '@core/components/modal/modal.vue';
import AProductCta from '@core/components/product-cta/product-cta.vue';
import ATooltip from '@core/components/tooltip/tooltip.vue';
import AYoutube from '@core/components/youtube/youtube.vue';
import { useBreakpoint } from '@core/composables/useBreakpoint';
import { useL10n } from '@core/composables/useL10n';
import { usePurchasing } from '@core/composables/usePurchasing';
import Modal from '@core/directives/modal.js';
import STableHeadCell from './components/table-head-cell.vue';
import STableHeadSwitcher from './components/table-head-switcher.vue';
import STableMobileHeadCell from './components/table-mobile-head-cell.vue';

defineOptions({
    name: 'ATableNew',
    directives: { Modal },
});

const props = defineProps({
    translations: {
        type: Object,
        default: () => ({}),
    },

    translated: {
        type: Object,
        default: undefined,
    },

    settings: {
        type: Object,
        required: true,
    },

    head: {
        type: Array,
        required: true,
    },

    rows: {
        type: Array,
        required: true,
    },

    theme: {
        type: String,
        validator: (prop) => ['light', 'dark'].includes(prop),
        default: 'light',
    },
});

const TEXT_CELL_TYPES = new Set(['subtitle', 'category', 'text']);
const getExpandKey = (rowIndex, cellIndex) => `row_${rowIndex}_cell_${cellIndex}`;

const { l10n } = useL10n(props);
const { currentBreakpoint, isDesktop } = useBreakpoint();
const { initSettingsCTA } = usePurchasing({
    productCurrency: computed(() => undefined),
});

const withOptionalRows = ref(true);
const submenuOnPage = ref(false);
const countHeadStylesThrottled = ref(null);
const expandedRowsLookup = ref({});
const syncScrollCleanup = ref(null);
const headRef = ref(null);
const bodyRef = ref(null);

const effectiveMobileType = computed(() => l10n.value?.settings?.mobileType ?? props.settings?.mobileType ?? '');

const className = computed(() => ({
    [`a-table_layout_${l10n.value.settings.layout}`]: l10n.value.settings.layout,
    [`a-table_height_${l10n.value.settings.height}`]: l10n.value.settings.height,
    [`a-table_align_${l10n.value.settings.align}`]: l10n.value.settings.align,
    'a-table_sticky': l10n.value.settings.sticky,
    'a-table_without-optional': !withOptionalRows.value,
    'a-table_with_submenu': submenuOnPage.value,
    'a-table_mobile-type-column': effectiveMobileType.value === 'column',
    [`a-table_type-${l10n.value.settings.type}`]: l10n.value.settings.type,
    'a-table_lte-3-cols': l10n.value.rows[0].content.length <= 3,
    'theme-dark': props.theme === 'dark',
}));

const modalData = computed(() => {
    const youtubeIds = new Set();
    const demoModalIds = new Set();
    const demoModals = [];

    for (const row of l10n.value?.rows || []) {
        for (const cell of row?.content || []) {
            if (cell?.type !== 'button') continue;

            if (cell.val?.ytId) {
                youtubeIds.add(cell.val.ytId);
            }

            const modal = cell.val?.modal;
            if (modal?.id && modal?.src && !demoModalIds.has(modal.id)) {
                demoModalIds.add(modal.id);
                demoModals.push(modal);
            }
        }
    }

    return {
        youtubeIds: [...youtubeIds],
        demoModals,
    };
});

const youtubeIds = computed(() => modalData.value.youtubeIds);
const demoModals = computed(() => modalData.value.demoModals);

const teardownSyncScroll = () => {
    syncScrollCleanup.value?.();
    syncScrollCleanup.value = null;
};

const setupSyncScroll = () => {
    teardownSyncScroll();

    const headElement = headRef.value;
    const bodyElement = bodyRef.value;
    if (!headElement || !bodyElement) return;

    const state = new WeakMap([
        [headElement, { x: 0 }],
        [bodyElement, { x: 0 }],
    ]);

    const getMaxScrollLeft = (element) => element.scrollWidth - element.clientWidth;

    const syncByRate = (sourceElement, targetElement) => {
        const sourceState = state.get(sourceElement);
        const targetState = state.get(targetElement);
        if (!sourceState || !targetState) return;

        const sourceLeft = sourceElement.scrollLeft;
        const sourceMax = getMaxScrollLeft(sourceElement);
        const sourceRate = sourceMax > 0 ? sourceLeft / sourceMax : 0;
        const hasUserUpdate = sourceLeft !== sourceState.x;
        sourceState.x = sourceLeft;
        if (!hasUserUpdate) return;

        const targetMax = getMaxScrollLeft(targetElement);
        const nextTargetLeft = Math.round(sourceRate * targetMax);
        targetState.x = nextTargetLeft;

        if (Math.round(targetElement.scrollLeft - nextTargetLeft)) {
            const target = targetElement;
            target.scrollLeft = nextTargetLeft;
        }
    };

    const onHeadScroll = () => syncByRate(headElement, bodyElement);
    const onBodyScroll = () => syncByRate(bodyElement, headElement);

    headElement.addEventListener('scroll', onHeadScroll, { passive: true });
    bodyElement.addEventListener('scroll', onBodyScroll, { passive: true });

    syncScrollCleanup.value = () => {
        headElement.removeEventListener('scroll', onHeadScroll);
        bodyElement.removeEventListener('scroll', onBodyScroll);
    };
};

const isExpand = (cellIndex, rowIndex) => Boolean(expandedRowsLookup.value[getExpandKey(rowIndex, cellIndex)]);

const toggleExpand = (cellIndex, rowIndex) => {
    const key = getExpandKey(rowIndex, cellIndex);
    const isExpanded = Boolean(expandedRowsLookup.value[key]);

    if (isExpanded) {
        const nextLookup = { ...expandedRowsLookup.value };
        delete nextLookup[key];
        expandedRowsLookup.value = nextLookup;
        return;
    }

    expandedRowsLookup.value = {
        ...expandedRowsLookup.value,
        [key]: true,
    };
};

const isTextCell = (cell) => {
    const type = (cell?.type ?? '').toString().trim();
    return TEXT_CELL_TYPES.has(type);
};

const getButtonModalId = (val) => {
    if (!isDesktop.value) return null;
    if (val?.ytId) return `yt_id-${val.ytId}`;
    if (val?.modal?.id) return val.modal.id;
    if (val?.modalId) return val.modalId;
    return null;
};

const getClassNameForTableCell = (cell) => ({
    [`a-table__cell_colspan_${cell.colspan}`]: cell.colspan,
    [`a-table__cell_align_${cell.align}`]: cell.align,
    'a-table__cell_without_border': cell.withoutBorder,
});

const getClassNameForTableRow = (row) => ({
    'a-table__row_has-expandable': row.content && row.content?.some(cell => cell.expand),
    'a-table__row_optional': row.optional,
    'a-table__row_with-indent': row.subtitleIndent,
    'a-table__row_no-scroll': row.mobileType === 'no-scroll',
    'a-table__row_multirow': row.mobileType === 'multirow',
    [`a-table__row_vertical_${row.vertical}`]: row.vertical,
});

const countHeadStyles = () => {
    if (!headRef.value) return;

    const containerWidth = 1312;

    if (currentBreakpoint.value === 'desktopLarge') {
        headRef.value.style.margin = `0 ${
            (containerWidth - document.documentElement.clientWidth) / 2
        }px`;
        headRef.value.style.padding = `0 ${
            (document.documentElement.clientWidth - containerWidth) / 2
        }px`;
    } else {
        headRef.value.style = '';
    }
};

watch(
    () => l10n.value?.settings?.type,
    async () => {
        await nextTick();
        setupSyncScroll();
    },
);

onMounted(async () => {
    if (typeof window !== 'undefined') {
        await nextTick();
        setupSyncScroll();
    }

    if (l10n.value.settings.sticky) {
        const submenu = document.querySelector('.a-submenu');
        const productSticky = document.querySelector('.s-header-product-new');

        if (submenu || productSticky) {
            submenuOnPage.value = true;
        }

        if (currentBreakpoint.value === 'desktopLarge') {
            countHeadStyles();
        }

        countHeadStylesThrottled.value = throttle(countHeadStyles, 200);
        window.addEventListener('resize', countHeadStylesThrottled.value);
    }
});

onBeforeUnmount(() => {
    teardownSyncScroll();
    if (!l10n.value.settings.sticky) return;
    window.removeEventListener('resize', countHeadStylesThrottled.value);
    countHeadStylesThrottled.value?.cancel?.();
});

onServerPrefetch(async () => {
    if (!import.meta.env.SSR) return;

    const rows = props.translations?.rows || props.rows;
    await initSettingsCTA(rows.flatMap((item) => item?.content));
});
</script>

<style lang="pcss" scoped>
.a-table__wrapper_type-1 {
  overflow-x: auto;
  max-width: 100%;
    .a-table__scroll-container {
        overflow-x: auto;
        display: block;
        max-width: 100%;
    }
}

.a-table {
    tbody {
        display: block;
    }
    &_layout {
        &_8-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 9%, 9%;

                    flex: 0 0 9%;

                    &:first-of-type {
                        @mixin colspan 37%, 9%;

                        flex: 0 0 37%;
                    }
                }
            }
        }

        &_7-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 14.286%, 14.286%;

                    flex: 0 0 14.286%;
                }
            }
        }

        &_6-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;
                }
            }
        }

        &_6-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 33.33%, 33.33%;

                    flex: 0 0 33.3%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop-wide) {
                    @mixin colspan 20%, 20%;

                    flex: 0 0 20%;
                }
            }
        }

        &_5-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;

                    &:first-of-type {
                        @mixin colspan 33.33%, 16.66%;

                        flex: 0 0 33.33%;
                    }
                }
            }
        }

        &_5-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 70%;

                @media (--viewport-tablet) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;

                    &:first-of-type {
                        @mixin colspan 33.33%, 50%;

                        flex: 0 0 33.33%;

                        & > * {
                            padding-inline-end: 27%;
                        }
                    }
                }
            }
        }

        &_4-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }
            }
        }

        &_4-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;
                }

                @media (--viewport-desktop) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;

                    &:first-of-type {
                        @mixin colspan 50%, 16.66%;

                        flex: 0 0 50%;

                        & > * {
                            padding-inline-end: 35%;
                        }
                    }
                }
            }
        }

        &_4-3 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;

                    &:first-of-type {
                        @mixin colspan 50%, 16.66%;

                        flex: 0 0 50%;

                        & > * {
                            padding-inline-end: 18%;
                        }
                    }
                }

                @media (--viewport-desktop) {
                    &:first-of-type {
                        & > * {
                            padding-inline-end: 35%;
                        }
                    }
                }
            }
        }

        &_4-4 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 16.66%, 16.66%;

                    flex: 0 0 16.66%;

                    &:first-of-type {
                        @mixin colspan 50%, 16.66%;

                        flex: 0 0 50%;
                    }
                }
            }
        }

        &_3-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 33.33%, 33.33%;

                    flex: 0 0 33.33%;
                }
            }
        }

        &_3-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;

                    &:first-of-type {
                        @mixin colspan 50%, 25%;

                        flex: 0 0 50%;

                        & > * {
                            padding-inline-end: 18%;
                        }
                    }
                }

                @media (--viewport-desktop) {
                    &:first-of-type {
                        & > * {
                            padding-inline-end: 35%;
                        }
                    }
                }
            }
        }

        &_3-3 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;

                @media (--viewport-tablet) {
                    @mixin colspan 25%, 25%;

                    flex: 0 0 25%;

                    &:first-of-type {
                        @mixin colspan 50%, 25%;

                        flex: 0 0 50%;
                    }
                }
            }
        }

        &_2-1 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 50%, 50%;

                flex: 0 0 50%;
            }
        }

        &_2-2 {
            .a-table__cell,
            .a-table__head-cell {
                @mixin colspan 33.33%, 33.33%;

                flex: 0 0 33.33%;

                &:first-of-type {
                    @mixin colspan 66.66%, 33.33%;

                    flex: 0 0 66.66%;
                }
            }
        }
    }

    &_height {
        &_small {
            .a-table__cell {
                padding-top: 16px;
                padding-bottom: 16px;
            }
        }

        &_bigger {
            .a-table__cell {
                padding-top: 24px;
                padding-bottom: 24px;
            }
        }
    }

    &_sticky {
        .a-table {
            &__head {
                position: sticky;
                z-index: 1;
                top: 0;
                margin: 0 -16px;
                padding: 0 16px;
                box-shadow: var(--av-shadow-small);
                clip-path: inset(0 0 -20px 0);

                @media (--viewport-mobile-wide) {
                    margin: 0 -32px;
                    padding: 0 32px;
                }
                @media (--viewport-desktop-wide) {
                    margin: 0 -64px;
                    padding: 0 64px;
                }
            }

            &__head-cell {
                border-bottom: none;
            }
        }
    }

    &_with_submenu {
        &.a-table_sticky {
            .a-table {
                &__head {
                    top: 58px;
                    @media (--viewport-mobile-wide) {
                        top: 65px;
                    }
                }
            }
        }
    }

    &_without-optional {
        .a-table__row_optional {
            display: none;
        }
    }

    &_align {
        &_left {
            .a-table__cell,
            .a-table__head-cell {
                &:not(:first-of-type) {
                    text-align: start;
                }
            }
            .a-table__check {
                position: relative;
                height: 100%;

                &-icon {
                    top: 50%;
                    inset-inline-start: 0;
                    transform: translate(0, -50%);
                }
            }
        }

        &_center {
            .a-table__cell,
            .a-table__head-cell {
                &:not(:first-of-type) {
                    text-align: center;
                }
            }

            :deep(.cta-description) {
                justify-content: center;
            }
        }
    }

    &__head-mobile {
        display: none;
    }

    &_mobile-type-column {
        @media all and (max-width: 767px) {
            .a-table {
                &__head {
                    display: none;
                }

                &__category {
                    display: flex;
                }

                &__glyph {
                    margin-inline-end: 16px;
                }

                &__row {
                    flex-wrap: wrap;
                }

                &__cell {
                    flex-basis: 100%;

                    &:not(:last-child) {
                        border-bottom: 0;
                    }

                    &:last-child {
                        padding-bottom: 32px;
                        margin-bottom: 24px;
                    }
                }

                &__head-mobile {
                    display: block;
                }
            }
        }
    }

    .data-center-figure {
        fill: green;
    }

    &__head {
        display: block;
        overflow-x: scroll;
        background-color: var(--av-inversed-primary);
        scrollbar-width: none;
        margin: 0 -16px;
        padding: 0 calc(16px - 8px);

        &::-webkit-scrollbar {
            height: 0;
            background: rgba(255, 255, 255, 0);
        }

        @media (--viewport-mobile-wide) {
            margin: 0 -32px;
            padding: 0 calc(32px - 8px);
        }

        @media (--viewport-desktop-wide) {
            margin: 0 -64px;
            padding: 0 calc(64px - 8px);
        }

        @media (--viewport-desktop-large) {
            margin: 0 -8px;
            padding: 0;
        }
    }

    &__head-row {
        display: flex;

        &.a-table__head-row_no-scroll {
            @media all and (max-width: 767px) {
                .a-table {
                    &__head-cell {
                        flex-basis: auto;
                        flex-shrink: 1;
                    }
                }
            }
        }
    }

    &__head-cell {
        display: block;
        padding: 32px 8px;
        border-bottom: 1px solid var(--av-brand-secondary-light);
        overflow: hidden;

        &:first-of-type {
            text-align: start;
        }

        &_align {
            &_left {
                text-align: start !important;
            }

            &_center {
                text-align: center !important;
            }

            &_right {
                text-align: end !important;
            }
        }
    }

    &__row {
        display: flex;
        &_vertical {
            &_middle {
                .a-table__text {
                    display: flex;
                    align-items: center;
                    height: 100%;
                }
            }
            &_bottom {
                .a-table__text {
                    display: flex;
                    align-items: flex-end;
                    height: 100%;
                }
            }
        }
        &_has-expandable {
            &:hover {
                @media (--viewport-tablet) {
                    background: rgba(64, 139, 234, 0.05);
                }
            }
        }

        &_with-indent {
            padding-top: 40px;
        }

        &.a-table__row_no-scroll {
            @media all and (max-width: 767px) {
                .a-table {
                    &__cell {
                        flex-basis: auto;
                        flex-shrink: 1;
                        flex-grow: 1;

                        &:first-child {
                            flex-basis: 60%;
                        }
                    }
                }
            }
        }

        &.a-table__row_multirow {
            @media all and (max-width: 767px) {
                flex-wrap: wrap;

                .a-table {
                    &__cell {
                        &:first-child {
                            flex-basis: 100%;
                        }
                    }
                }
            }
        }
    }

    &__body {
        display: block;
        overflow-x: scroll;
        margin: 0 -16px;
        padding: 0 calc(16px - 8px);

        &::-webkit-scrollbar-thumb {
            background: rgb(193, 193, 193);
            border-radius: 3px;
        }
        &::-webkit-scrollbar {
            max-width: 6px;
            max-height: 6px;
        }

        @media (--viewport-mobile-wide) {
            margin: 0 -32px;
            padding: 0 calc(32px - 8px);
        }

        @media (--viewport-desktop-wide) {
            margin: 0 -64px;
            padding: 0 calc(64px - 8px);
        }

        @media (--viewport-desktop-large) {
            margin: 0 -8px;
            padding: 0;
        }
    }

    &__cell {
        display: block;
        position: relative;
        padding-inline-start: 8px;
        padding-inline-end: 8px;
        border-bottom: 1px solid var(--av-brand-secondary-light);
        overflow: hidden;

        &_align {
            &_left {
                text-align: start !important;
            }

            &_left :deep(.cta-description) {
                justify-content: start;
            }

            &_center {
                text-align: center !important;

                .a-table__subtitle {
                    justify-content: center;
                }
            }

            &_right {
                text-align: end !important;
            }

            &_right :deep(.cta-description) {
                justify-content: end;
            }
        }

        &_without_border {
            border: none;
        }

        :deep(a:not(.a-button, .a-link)) {
            color: var(--av-brand-primary);
            text-decoration: none;

            del {
                color: var(--av-brand-primary);
            }

            &:hover {
                color: var(--av-brand-secondary);
            }
            &:focus {
                background: var(--av-brand-secondary-light);
                color: var(--av-brand-primary);
            }
            &:active {
                background: none;
                color: var(--av-brand-primary);
            }
        }
    }

    &__subtitle {
        position: relative;
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 700;
        line-height: 32px;
    }

    &__tooltip {
        position: relative;
        top: 3px;
        margin-inline-start: 8px;
        vertical-align: baseline;
    }

    &__glyph {
        display: block;
        margin-bottom: 8px;
        width: 24px;
        height: 24px;
    }

    &__category {
        @mixin paragraph;

        vertical-align: middle;
        font-weight: 600;
        color: var(--av-nav-primary);
    }

    &__category :deep(.cta-description) {
        font-weight: 600;
    }

    &__expand {
        display: flex;
        align-items: center;
        padding-inline-end: 16px !important;
        user-select: none;
        cursor: pointer;
        :deep(.toggleIcon) {
            pointer-events: none;
            margin-inline-end: 16px;
            min-width: 16px;
            vertical-align: middle;
        }
        :deep(.a-table__tooltip) {
            top: 0;
            vertical-align: middle;
        }
        & + .a-table__description {
            margin-top: 16px;
            padding-inline-start: 32px;
            padding-inline-end: 16px !important;
        }
    }

    &__description {
        margin-top: -8px;
        padding-inline-start: 48px;
        position: relative;

        &-icon {
            top: 0;
            inset-inline-start: 0;
            width: 32px;
            height: 32px;
            position: absolute;
        }

        &-title {
            @mixin paragraph-accent;

            margin-top: 8px;
            color: var(--av-nav-primary);
        }

        &-text {
            margin-top: 8px;
        }

        &-link {
            @mixin body-accent;

            margin-top: 8px;

            .a-link {
                &__glyph_position_right {
                    margin-inline-start: 8px;
                }
            }
        }

        &-note {
            @mixin body;

            margin-top: 8px;
            color: var(--av-fixed-light);
        }
    }

    &__description-full {
        padding: 0 !important;
    }

    &__text {
        color: var(--av-fixed-secondary);
    }

    &__list {
        list-style-type: none;
    }

    &__list-item {
        margin-bottom: 16px;

        &:last-child {
            margin-bottom: 8px;
        }
    }

    &__check {
        min-width: 12px;
    }

    &__check-icon {
        position: absolute;
        top: 50%;
        inset-inline-start: 50%;
        transform: translate(-50%, -50%);

        &_true {
            width: 12px;
            height: 12px;
            border-radius: 100%;
            background: var(--av-fixed-success);
        }

        &_false {
            width: 16px;
            height: 2px;
            background-color: var(--av-brand-secondary-light);
        }
    }

    &__button-wrapper {
        padding: 16px 0;
    }

    &__button-wrapper :deep(.a-button) {
        &:not(.auto-width) {
            width: 100%;
        }
    }

    i,
    em {
        font-style: italic;
    }
    b {
        font-weight: bold;
    }
    strong {
        font-weight: 600;
    }

    :deep(.cta) {
        font: var(--font-default);
    }

    :deep(.cta-description) {
        margin: 0;
    }

    .a-table__subtitle :deep(.cta) {
        font-size: 18px;
        line-height: 32px;
    }

    &__lead {
        @mixin paragraph;
        margin-top: 48px;
        margin-bottom: 40px;
        color: #384A63;

        @media (--viewport-tablet) {
            margin-bottom: 56px;
        }

        @media (--viewport-desktop) {
            max-width: 632px;
        }

        @media (--viewport-desktop-wide) {
            max-width: 760px;
        }

        @media (--viewport-desktop-large) {
            max-width: 870px;
        }
    }

    &_type-1 {
        .a-table__head-cell,
        .a-table__cell {
            border-bottom: 1px solid rgba(56, 74, 99, .2);

            &:first-child {
                box-shadow: 3px 0 2px 0 rgba(230, 231, 233, .5);

                @media (--viewport-tablet) {
                    box-shadow: none;
                }
            }

            &:not(:first-of-type) {
                padding: 24px 12px;
            }
        }

        :deep(.a-table__head-text) {
            @mixin lead;
            font-weight: 600;
        }

        .a-table__text {
            color: #384A63
        }
    }

    &_lte-3-cols {
        .a-table__head-cell:first-child,
        .a-table__cell:first-child {
            flex-grow: 1;
        }
    }
}

.a-table_type-1 .syncscroll {
    overflow-x: unset;
}

.theme-dark {
    background-color: var(--av-nav-primary);
    .a-table,
    .a-table__body,
    .a-table__head {
        background-color: var(--av-nav-primary);
    }
    .a-table__cell,
    .a-table__head-cell {
        border-color: var(--av-inversed-primary);
    }
    .a-table__check-icon_false {
        background-color: var(--av-inversed-primary);
    }
    .a-table__lead,
    .a-table__text,
    .a-table__head,
    .a-table__category,
    :deep(.a-table__head-text),
    :deep(.a-table__head-desc),
    .a-table__subtitle,
    :deep(.a-table-switcher__text) {
        color: var(--av-inversed-primary);
    }
}
</style>

<style lang="pcss">
.s-slice_background_dark-blue,
.s-slice_background_light-blue,
.s-slice_background_n-p-gradient-top,
.s-slice_background_l-b-gradient-top,
.s-slice_background_l-b-gradient-bottom,
.s-slice_background_navy-gradient {
    .a-table__head-row {
        background: transparent;
    }
}

body.has-anchor-menu {
    .a-table_sticky {
        --table-sticky-offset: var(--anchor-menu-sticky-bottom, 0px);

        &.a-table_with_submenu {
            --table-sticky-offset: max(var(--anchor-menu-sticky-bottom, 0px), 58px);

            @media (--viewport-mobile-wide) {
                --table-sticky-offset: max(var(--anchor-menu-sticky-bottom, 0px), 65px);
            }
        }

        .a-table__head {
            top: var(--table-sticky-offset) !important;
        }
    }
}
</style>
